import { BitMask64Utils } from '../../Utils/BigIntCompatibility';
import { createLogger } from '../../../Utils/Logger';
import { getComponentTypeName } from '../../Decorators';
/**
 * 组件注册表
 * 管理组件类型的位掩码分配
 */
export class ComponentRegistry {
    /**
     * 注册组件类型并分配位掩码
     * @param componentType 组件类型
     * @returns 分配的位索引
     */
    static register(componentType) {
        const typeName = getComponentTypeName(componentType);
        if (this.componentTypes.has(componentType)) {
            const existingIndex = this.componentTypes.get(componentType);
            return existingIndex;
        }
        const bitIndex = this.nextBitIndex++;
        this.componentTypes.set(componentType, bitIndex);
        this.bitIndexToType.set(bitIndex, componentType);
        this.componentNameToType.set(typeName, componentType);
        this.componentNameToId.set(typeName, bitIndex);
        return bitIndex;
    }
    /**
     * 获取组件类型的位掩码
     * @param componentType 组件类型
     * @returns 位掩码
     */
    static getBitMask(componentType) {
        const bitIndex = this.componentTypes.get(componentType);
        if (bitIndex === undefined) {
            const typeName = getComponentTypeName(componentType);
            throw new Error(`Component type ${typeName} is not registered`);
        }
        return BitMask64Utils.create(bitIndex);
    }
    /**
     * 获取组件类型的位索引
     * @param componentType 组件类型
     * @returns 位索引
     */
    static getBitIndex(componentType) {
        const bitIndex = this.componentTypes.get(componentType);
        if (bitIndex === undefined) {
            const typeName = getComponentTypeName(componentType);
            throw new Error(`Component type ${typeName} is not registered`);
        }
        return bitIndex;
    }
    /**
     * 检查组件类型是否已注册
     * @param componentType 组件类型
     * @returns 是否已注册
     */
    static isRegistered(componentType) {
        return this.componentTypes.has(componentType);
    }
    /**
     * 通过位索引获取组件类型
     * @param bitIndex 位索引
     * @returns 组件类型构造函数或null
     */
    static getTypeByBitIndex(bitIndex) {
        return this.bitIndexToType.get(bitIndex) || null;
    }
    /**
     * 获取当前已注册的组件类型数量
     * @returns 已注册数量
     */
    static getRegisteredCount() {
        return this.nextBitIndex;
    }
    /**
     * 通过名称获取组件类型
     * @param componentName 组件名称
     * @returns 组件类型构造函数
     */
    static getComponentType(componentName) {
        return this.componentNameToType.get(componentName) || null;
    }
    /**
     * 获取所有已注册的组件类型
     * @returns 组件类型映射
     */
    static getAllRegisteredTypes() {
        return new Map(this.componentTypes);
    }
    /**
     * 获取所有组件名称到类型的映射
     * @returns 名称到类型的映射
     */
    static getAllComponentNames() {
        return new Map(this.componentNameToType);
    }
    /**
     * 通过名称获取组件类型ID
     * @param componentName 组件名称
     * @returns 组件类型ID
     */
    static getComponentId(componentName) {
        return this.componentNameToId.get(componentName);
    }
    /**
     * 注册组件类型（通过名称）
     * @param componentName 组件名称
     * @returns 分配的组件ID
     */
    static registerComponentByName(componentName) {
        if (this.componentNameToId.has(componentName)) {
            return this.componentNameToId.get(componentName);
        }
        const bitIndex = this.nextBitIndex++;
        this.componentNameToId.set(componentName, bitIndex);
        return bitIndex;
    }
    /**
     * 创建单个组件的掩码
     * @param componentName 组件名称
     * @returns 组件掩码
     */
    static createSingleComponentMask(componentName) {
        const cacheKey = `single:${componentName}`;
        if (this.maskCache.has(cacheKey)) {
            return this.maskCache.get(cacheKey);
        }
        const componentId = this.getComponentId(componentName);
        if (componentId === undefined) {
            throw new Error(`Component type ${componentName} is not registered`);
        }
        const mask = BitMask64Utils.create(componentId);
        this.maskCache.set(cacheKey, mask);
        return mask;
    }
    /**
     * 创建多个组件的掩码
     * @param componentNames 组件名称数组
     * @returns 组合掩码
     */
    static createComponentMask(componentNames) {
        const sortedNames = [...componentNames].sort();
        const cacheKey = `multi:${sortedNames.join(',')}`;
        if (this.maskCache.has(cacheKey)) {
            return this.maskCache.get(cacheKey);
        }
        const mask = BitMask64Utils.clone(BitMask64Utils.ZERO);
        for (const name of componentNames) {
            const componentId = this.getComponentId(name);
            if (componentId !== undefined) {
                const componentMask = BitMask64Utils.create(componentId);
                BitMask64Utils.orInPlace(mask, componentMask);
            }
        }
        this.maskCache.set(cacheKey, mask);
        return mask;
    }
    /**
     * 清除掩码缓存
     */
    static clearMaskCache() {
        this.maskCache.clear();
    }
    /**
     * 重置注册表（用于测试）
     */
    static reset() {
        this.componentTypes.clear();
        this.bitIndexToType.clear();
        this.componentNameToType.clear();
        this.componentNameToId.clear();
        this.maskCache.clear();
        this.nextBitIndex = 0;
    }
}
ComponentRegistry._logger = createLogger('ComponentStorage');
ComponentRegistry.componentTypes = new Map();
ComponentRegistry.bitIndexToType = new Map();
ComponentRegistry.componentNameToType = new Map();
ComponentRegistry.componentNameToId = new Map();
ComponentRegistry.maskCache = new Map();
ComponentRegistry.nextBitIndex = 0;
//# sourceMappingURL=ComponentRegistry.js.map